import type {
  GenerateShadesHexType,
  GenerateShadesParamsType,
  GenerateShadesReturnType,
  GenerateShadesStringOpts,
  NonEmptyRecord,
} from "@common/types/commonTypes/generateShades";

import type {
  ScrollToAnchorOptions,
  CommonPaginationOptions,
  handleImageRatioInterface,
  ImageRadioType,
  renderEl,
  BootstrapPlugins,
} from "@common/types/commonTypes/functions";
import { registerInit } from "@common/bootstrapComponents/util.mjs";

export const registerBootstrap = (...registers: BootstrapPlugins[]) => {
  registers.forEach((plugin) => {
    const { constructor = undefined, init = undefined, name = "" } = plugin;
    if (!window.$) {
      console.error("window环境中不存在jQuery实例。");
      return;
    }
    if (!name) {
      console.error(plugin, `没有被命名。`);
      return;
    }
    if (window.$.fn[name]) {
      console.error(`重复注册的${name.toString()}插件。`);
      return;
    }
    if (!constructor) {
      console.error(`${name.toString()}插件，构造原型不存在。`);
      return;
    }
    if (!init) {
      console.error(`${name.toString()}插件，初始化函数不存在。`);
      return;
    }
    registerInit();
    init();
    const interfaceMethod = constructor._jQueryInterface;
    if (typeof interfaceMethod === "function") {
      // 注册核心方法
      window.$.fn[name] = interfaceMethod;
      // // 同时注册完整接口属性
      window.$.fn[name].Constructor = plugin.constructor;
      // 如果存在 noConflict 方法，也注册上去
      window.$.fn[name].noConflict = () => {
        window.$.fn[name] = window.$.fn[name];
        return plugin.constructor._jQueryInterface;
      };
    }
    if (!window.registerPlugins) {
      window.registerPlugins = [];
    }
    window.registerPlugins.push(name);
  });
};

export function lazyLoad(offsetOrSelector: string = "50%", onLoad?: () => void) {
  const selectorString = "img[yige-src],video[yige-src],iframe[yige-src]";
  const excludeSelectorString = "*[exclude-lazy]";
  const imgList = $(selectorString).not(excludeSelectorString);
  const backgroundList = $("*[yige-background]");
  let observer: IntersectionObserver | null = null;
  // 定义加载逻辑
  const loading: IntersectionObserverCallback = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const el = $(entry.target);
        setAttribute(el);
        observer.unobserve(entry.target);
      }
    });
  };

  const setAttribute = (el: JQuery<Element>) => {
    const SRC = el.attr("yige-src") as string | undefined;
    const BACKGROUND = el.attr("yige-background");
    if (SRC) {
      if (onLoad) {
        el.one("load", onLoad);
      }
      el.attr("src", SRC);
      el.removeAttr("yige-src");
    }
    if (BACKGROUND) {
      el.css("background-image", `url(${BACKGROUND})`);
      el.removeAttr("yige-background");
    }
  };

  // 初始化 IntersectionObserver
  if ("IntersectionObserver" in window) {
    observer = new IntersectionObserver(loading, {
      root: null,
      rootMargin: `0px 0px ${offsetOrSelector} 0px`,
      threshold: 0,
    });
    // 观察初始元素
    imgList.each((_, el) => observer?.observe(el));
    backgroundList.each((_, el) => observer?.observe(el));
  } else {
    // 不支持 IntersectionObserver 的情况下，立即加载所有元素
    imgList.each((_, img) => {
      setAttribute($(img));
    });

    backgroundList.each((_, background) => {
      setAttribute($(background));
    });
  }

  // 定义 update 方法
  const update = (selector?: JQuery<Element>) => {
    // 如果调用update时传入了选择器，则直接赋值该选择器下的所有懒加载元素
    if (selector && selector.length) {
      selector.find(selectorString).each((_, el) => {
        setAttribute($(el));
      });
      return;
    }
    // 如果有 IntersectionObserver，重新收集并监听
    if (observer) {
      // 断开所有正在监听的元素
      observer.disconnect();

      // 重新收集元素
      const newImgList = $(selectorString).not(excludeSelectorString);
      const newBackgroundList = $("*[yige-background]");
      // 重新监听
      newImgList.each((_, el) => observer?.observe(el));
      newBackgroundList.each((_, el) => observer?.observe(el));
    } else {
      // 不支持 IntersectionObserver 的情况下，重新立即加载
      const newImgList = $(selectorString);
      newImgList.each((_, img) => {
        setAttribute($(img));
      });
      const newBackgroundList = $("*[yige-background]");
      newBackgroundList.each((_, background) => {
        setAttribute($(background));
      });
    }
  };

  // 返回 update 方法
  return { update };
}
/**
 * @function resetImgSize
 * @deprecated 该方法已废弃，请使用 handleImageRatio 方法代替
 */
export function resetImgSize({ nodeList, setImg = true, setBox = false, cb }: ResetImgSizeFn) {
  const temp: any[] = [];
  nodeList.each((_i, img) => {
    if (!$(img).is("img")) return;
    function resizeImage(image: HTMLImageElement) {
      const originalH = image.naturalHeight / 10 + "rem";
      const originalW = image.naturalWidth / 10 + "rem";
      if (setBox) {
        $(image).parents(".img-box").eq(0).css({
          height: originalH,
          width: originalW,
        });
      }
      if (setImg) {
        $(image).css({
          height: originalH,
          width: originalW,
        });
      }
      temp.push({
        item: img,
        w: originalW,
        h: originalH,
      });
      if (cb && typeof cb === "function") cb();
    }
    if ((img as HTMLImageElement).complete && (img as HTMLImageElement).src) {
      // 如果图片已加载
      resizeImage(img as HTMLImageElement);
    } else {
      $(img).on("load", function () {
        resizeImage(this as HTMLImageElement);
      });
    }
  });
  return temp;
}
/**
 * @function handleImageRatio
 * @param {JQuery<HTMLImageElement>} Els - 需要处理的图片元素集合
 * @param {handleImageRatioInterface} opts - 处理选项
 * @returns {Promise<ImageRadioType[]>} - 返回一个 Promise，解析为包含图片原始尺寸和比例的数组
 * @description 该函数用于处理图片的原始尺寸和比例，并根据选项自动设置对应容器的宽高或宽高比。
 */
export async function handleImageRatio(Els: JQuery<HTMLImageElement>, opts?: handleImageRatioInterface) {
  const { auto = true, cb = undefined, unit = "radio", setTo = ".img-box" } = opts || {};
  const gcd = (a: number, b: number): number => {
    return b === 0 ? a : gcd(b, a % b);
  };
  const promises: Promise<ImageRadioType>[] = [];
  const getOriginalSize = (image: HTMLImageElement, resolve: (value: ImageRadioType) => void) => {
    const originalH = image.naturalHeight;
    const originalW = image.naturalWidth;
    let setTarget: HTMLElement = image;
    const divisor = gcd(originalW, originalH);
    const ratio = (originalW / divisor).toString() + "/" + (originalH / divisor).toString();

    if (cb && typeof cb === "function") cb();
    if (auto) {
      if (setTo.trim() !== "") {
        setTarget = $(image).closest(setTo).get(0) as HTMLElement;
      }
      if (unit === "radio") {
        $(setTarget).css({
          aspectRatio: ratio,
          width: "100%",
          height: "100%",
        });
      } else if (unit === "size") {
        $(setTarget).css({
          width: originalW / 10 + "rem",
          height: originalH / 10 + "rem",
        });
      }
    }

    resolve({
      item: image,
      w: originalW,
      h: originalH,
      ratio,
    });
  };
  Els.each((idx, img) => {
    const p = new Promise<ImageRadioType>((resolve) => {
      if ((img as HTMLImageElement).complete && (img as HTMLImageElement).src) {
        // 如果图片已加载
        getOriginalSize(img as HTMLImageElement, resolve);
      } else {
        $(img).one("load", function () {
          getOriginalSize(this as HTMLImageElement, resolve);
        });
      }
    });
    promises.push(p);
  });
  return Promise.all(promises);
}
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number = 200,
  options?: {
    leading?: boolean;
    trailing?: boolean;
  },
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;
  let lastInvokeTime: number | null = null;
  // 保存最后的上下文，后面用于调用 func
  let lastContext: any;

  const { leading = true, trailing = true } = options || {};

  // 改进：将 invokeFunc 改为接受 context 和 args 参数
  const invokeFunc = (time: number, context: any, args: Parameters<T>) => {
    lastInvokeTime = time;
    func.apply(context, args);
    lastArgs = null;
    lastContext = null;
  };

  const timerExpired = () => {
    const now = Date.now();
    if (lastArgs && trailing) {
      // 注意这里用 lastContext，而非 this，因为 timerExpired 没有绑定正确的上下文
      invokeFunc(now, lastContext, lastArgs);
    }
    timeout = null;
  };

  return function throttled(this: any, ...args: Parameters<T>) {
    const now = Date.now();
    // 如果没有设置过上次执行时间，并且 leading 为 false，则初始化 lastInvokeTime
    if (lastInvokeTime === null && !leading) {
      lastInvokeTime = now;
    }
    const remaining = wait - (now - (lastInvokeTime || 0));
    // 保存当前调用的参数和上下文
    lastArgs = args;
    /* 
      ESLint 警告“Unexpected aliasing of 'this' to local variable”，
      在这种场景下我们需要捕获 this 来保证 func 的调用上下文，
      因此在此行禁用该规则：
    */
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastContext = this;

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      invokeFunc(now, lastContext, lastArgs);
    } else if (!timeout && trailing) {
      timeout = setTimeout(timerExpired, remaining);
    }
  };
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number = 200,
  options?: {
    immediate?: boolean;
  },
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const { immediate = false } = options || {};

  return function debounced(this: any, ...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (!immediate) {
        func.apply(this, args);
      }
    };

    const callNow = immediate && !timeout;

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) {
      func.apply(this, args);
    }
  };
}

export function handleSendInquiry({ formId, trigger, opts }: SendInquiryOpts) {
  const formEl = $(formId)[0] as HTMLFormElement;
  const { successUrl = "/page/thanks.html" } = opts || {};
  const beforeSubmit = (data: FormData) => {
    let FLAG = true;
    const disableFields: any = {};
    const formElements = formEl.elements;
    for (let i = 0; i < formElements.length; i++) {
      const element = formElements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      const key = element.name;
      const type = element.type;
      const is_required = element.required;
      let msg: string | undefined = undefined;
      if (opts && opts.inquiryMsg && opts.inquiryMsg[key]) {
        msg = opts.inquiryMsg[key];
      }
      if (type === "file" && (data.get(key)! as File).name === "") {
        data.delete(key);
      }
      if (type === "checkbox" && !data.get(key)) {
        if (is_required) {
          FLAG = false;
          rejectMessage(key);
          break;
        } else {
          data.delete(key);
        }
      }
      if (element.disabled && element.value) {
        data.set(key, element.value);
      }
      if (is_required && element.value === "") {
        FLAG = false;
        rejectMessage(key, msg);
        break;
      }
    }
    /*! custom Setting Start */
    if (opts && opts.fn && typeof opts.fn === "function") {
      const res = opts.fn({ data, disableFields, rejectMessage });
      if (typeof res === "boolean") return res;
    }
    return FLAG;
  };
  const rejectMessage = (key: string, msg?: string) => {
    formEl[key].focus();
    console.log(msg);
    alert(!msg || msg === "" ? `${key} can not be empty!` : msg);
  };
  const sendAjax = (data: FormData, originalTrigger: HTMLElement) => {
    $.ajax({
      processData: false,
      contentType: false,
      type: "POST",
      url: "/inquiry",
      data: data,
      success: function (jsonResult) {
        if (jsonResult.code == 200) {
          ($(formEl)[0] as HTMLFormElement).reset();
          if (opts && opts.onSuccess && typeof opts.onSuccess === "function") {
            opts.onSuccess(jsonResult, originalTrigger);
          } else {
            location.href = successUrl;
          }
        } else {
          if (opts && opts.onReject && typeof opts.onReject === "function") {
            opts.onReject(jsonResult, originalTrigger);
          } else {
            alert(jsonResult.msg);
          }
        }
        $(":input[name = '__token__']").val(jsonResult.data.token);
      },
      error: function (data) {
        if (opts && opts.onReject && typeof opts.onReject === "function") {
          opts.onReject(data, originalTrigger);
        }
      },
    });
  };
  $(formEl)
    .find("input,textarea,select")
    .on("blur", (e) => {
      const target = $(e.currentTarget);
      const parent = target.parents(".input-box");
      if (target.val()) {
        parent.addClass("is-valid");
      } else {
        parent.removeClass("is-valid");
      }
    });
  $(trigger).on("click", () => {
    const triggerElement = $(formEl).data("triggerElement");
    const formData = new FormData(formEl);
    const readyToSend = beforeSubmit(formData);
    if (readyToSend) {
      if (opts && opts.onBeforeSend && typeof opts.onBeforeSend === "function") {
        opts.onBeforeSend(formData);
      }
      sendAjax(formData, triggerElement);
    }
  });
}
export function INIT({ h, searchTriggle, headerSelect = "header" }: { h?: number; searchTriggle?: string; headerSelect?: string }) {
  const isMobile = $(window).width()! <= 1024;
  const header = $(headerSelect);
  const scrollTopButton = $(".scroll-top");
  const searchBox = $(".search-box");
  const initHeaderSticky = (height: number): void => {
    $(window).on("scroll", () => {
      if ($(window).scrollTop()! > height) {
        header.addClass("fixeds");
        scrollTopButton.show(400);
      } else {
        header.removeClass("fixeds");
        scrollTopButton.hide(400);
      }
    });
  };
  const initSearch = (): void => {
    if (!searchBox.length || !searchTriggle) return;
    $(`header ${searchTriggle}`).on("click", (event) => {
      searchBox.slideToggle(500);
      $(document).one("click", () => {
        searchBox.slideUp(500);
      });
      event.stopPropagation();
    });
    searchBox.on("click", (event) => {
      event.stopPropagation();
    });
  };
  const initMobileMenu = (): void => {
    $(".header .trigger").on("click", function () {
      $(this).toggleClass("trigger_active");
      $(this).siblings(".content_mt_ul").slideToggle(300);
    });
    $(".header .mobile_nav .nav_bg").on("click", function () {
      $("html").css("overflow-y", "unset");
      $(".mobile_nav .mobile_nav_content").removeClass("nav_show");
    });
    $(".header .mobile_nav .cus-bars").on("click", function () {
      $("html").css("overflow-y", "hidden");
      $(".mobile_nav .mobile_nav_content").addClass("nav_show");
    });
  };
  scrollTopButton.on("click", () => {
    $(window).scrollTop(0);
  });
  if (!isMobile && h && typeof h === "number") {
    initHeaderSticky(h);
  }
  if (!isMobile && searchTriggle && typeof searchTriggle === "string") {
    initSearch();
  }
  if (isMobile) {
    initMobileMenu();
  }
}
// -- ImportBundle 重载签名：仅对参数2 src 做重载 --
export function ImportBundle(options: BundleImporter.BundleOptions, src: string[]): Promise<boolean>;
export function ImportBundle(options: BundleImporter.BundleOptions, src: BundleImporter.SrcOptions[]): Promise<boolean>;
export function ImportBundle(options: BundleImporter.BundleOptions, src: BundleImporter.SrcOptions[] | string[]): Promise<boolean> {
  // -- 提前退出条件 --
  const { pathname } = window.location;
  const isIndexPage = pathname === "/" || /index.html$/g.test(pathname);

  if (!options.isIndex && !options.onlyIndex && isIndexPage) return Promise.resolve(false);
  if (options.onlyIndex && !isIndexPage) return Promise.resolve(false);
  if (options.isMobile && document.body.clientWidth < 768) return Promise.resolve(false);

  // -- 归一化 & 去重 --
  const seen = new Set<string>();
  const items: BundleImporter.SrcOptions[] = [];

  for (const raw of src) {
    const item: BundleImporter.SrcOptions = typeof raw === "string" ? { src: raw } : raw;
    if (!item?.src) continue;
    if (seen.has(item.src)) continue;
    seen.add(item.src);

    // 移动端专属资源在 PC 端跳过
    if (item.isMobile && document.body.clientWidth >= 768) continue;

    items.push(item);
  }

  const total = items.length;
  if (total === 0) {
    options.callback?.();
    return Promise.resolve(true);
  }

  // -- 构建 DOM --
  const isCss = options.type === "css";
  const fragment = document.createDocumentFragment();
  let loaded = 0;

  return new Promise<boolean>((resolve) => {
    const onResourceReady = () => {
      loaded++;
      if (loaded >= total) {
        options.callback?.();
        resolve(true);
      }
    };

    for (const item of items) {
      if (isCss) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = item.src;
        link.onload = onResourceReady;
        link.onerror = onResourceReady;
        fragment.appendChild(link);
      } else {
        const script = document.createElement("script");
        script.src = item.src;
        script.defer = item.defer ?? false;
        script.async = item.async ?? false;
        script.onload = () => {
          item.fn?.();
          onResourceReady();
        };
        script.onerror = onResourceReady;
        fragment.appendChild(script);
      }
    }

    const head = document.head;
    const refNode = head.querySelector('link[rel="stylesheet"]');
    if (options.position === "end") {
      head.appendChild(fragment);
    } else {
      head.insertBefore(fragment, refNode);
    }
  });
}
export function isMobile(w: number = 620): boolean {
  const windowWidth = $(window).width()!;
  return windowWidth <= w;
}
const preventDefault = (e: Event) => {
  e.preventDefault();
};
export function catchWheelEvent(enable: boolean) {
  if (enable) {
    $("html").css("overflow-y", "hidden");
    document.addEventListener("wheel", preventDefault, { passive: false });
    document.addEventListener("touchmove", preventDefault, { passive: false });
  } else {
    $("html").css("overflow-y", "initial");
    document.removeEventListener("wheel", preventDefault);
    document.removeEventListener("touchmove", preventDefault);
  }
}
/**
 * 依赖 "--item" css变量
 * @param {JQuery<HTMLElement>} list
 */
export function listToGroup<T extends JQuery>(list: T, bp?: number) {
  const breakpoint = bp ?? Number(list.css("--item"));
  const tempList: T[] = [];
  for (let i = 0; i < list.length; i += breakpoint) {
    const group = list.slice(i, i + breakpoint);
    tempList.push(group);
  }
  return tempList;
}

export function handleOldProjectInquiry({ formId, trigger, opts }: SendInquiryOpts) {
  const formEl = $(formId)[0] as HTMLFormElement;
  const tokenKey = "500f71851f22f13c914c7a0747f19ab8";
  let isSending = false;
  $(trigger).on("click", (e) => {
    const formData = new FormData(formEl);
    const readyToSend = beforeSubmit(formData);
    if (!readyToSend) {
      e.preventDefault();
      return false;
    }
    if (opts && opts.onBeforeSend && typeof opts.onBeforeSend === "function") {
      opts.onBeforeSend(formData);
    }
    isSending = true;
  });
  $(formId).on("change", (e) => {
    const formData = new FormData(formEl);
    const key = formData.get("name") + tokenKey + formData.get("email");
    // @ts-ignore
    const token = $.md5(key);
    $(e.currentTarget).find("input[name='token']").attr("value", token);
  });
  const beforeSubmit = (data: FormData) => {
    if (isSending) {
      window.alert("Message is being sent, please wait!");
      return false;
    }
    let FLAG = true;
    const disableFields: any = {};
    const formElements = formEl.elements;
    for (let i = 0; i < formElements.length; i++) {
      const element = formElements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      const key = element.name;
      const type = element.type;
      const is_required = element.required;
      if (type === "file" && (data.get(key)! as File).name === "") {
        data.delete(key);
      }
      if (type === "checkbox" && !data.get(key)) {
        if (is_required) {
          FLAG = false;
          rejectMessage(key);
          break;
        } else {
          data.delete(key);
        }
      }
      if (element.disabled && element.value) {
        data.set(key, element.value);
      }
      if (is_required && element.value === "") {
        let msg: string | undefined = undefined;
        if (opts && opts.inquiryMsg && opts.inquiryMsg[key]) {
          msg = opts.inquiryMsg[key];
        }
        FLAG = false;
        rejectMessage(key, msg);
        break;
      }
      if (key === "email" && element.value.indexOf("@") < 0) {
        FLAG = false;
        rejectMessage(key, "The Email is wrong");
        break;
      }
    }
    /*! custom Setting Start */
    if (opts && opts.fn && typeof opts.fn === "function") {
      const res = opts.fn({ data, disableFields, rejectMessage });
      if (typeof res === "boolean") return res;
    }
    return FLAG;
  };
  const rejectMessage = (key: string, msg?: string) => {
    formEl[key].focus();
    alert(msg ?? `${key} can not be empty!`);
    return false;
  };
}

/**
 * @description 从 img 元素加载并返回对应的 SVG 元素
 * @param {HTMLImageElement} img - 目标 img 元素
 * @param {string} target - 用于获取 SVG 源码的接口地址，默认为 "/custom/getUrlOrigin"
 * @returns {Promise<SVGElement | null>} 返回一个 Promise，解析为 SVG 元素或 null（如果不是 SVG）
 */
export function fetchSVGfromImg(img: HTMLImageElement, target: string = "/custom/getUrlOrigin"): Promise<SVGElement | null> {
  return new Promise(async (resolve, reject) => {
    let src = $(img).attr("yige-src") || $(img).attr("src");
    if (src && !src.endsWith(".svg")) return resolve(null);

    // 若 src 缺少 host（以 / 开头的相对路径），补全当前页面的 origin
    if (src && src.startsWith("/")) {
      src = `${location.origin}${src}`;
    }

    try {
      const res = await $.ajax({
        url: target,
        data: {
          url: src,
        },
        method: "get",
      });
      if (src !== res.data.src || !origin) return;
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(res.data.origin, "image/svg+xml");
      const svg = svgDoc.documentElement as unknown as SVGElement;
      return resolve(svg);
    } catch (error) {
      console.warn(error);
      reject(error);
    }
  });
}

/**
 * @description 将字符串转换为数字，若转换失败则返回备用值
 * @param {string | undefined} value - 需要转换的字符串
 * @param {number} fallback - 转换失败时返回的备用值
 * @returns {number} 转换后的数字或备用值
 */
export const toNumber = (value: string | number | undefined, fallback: number): number => {
  if (value === undefined || value === null || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * @description 为指定的页面区块添加唯一的 ID 属性
 * @param {string} sectionMarker - 用于标识区块类型的 data 属性值，默认为 "section"
 * @param {string} [idMarker] - 用于生成 ID 的前缀，默认为 sectionMarker 的值
 */
export const initSectionAnchor = (sectionMarker: string = "section", idMarker?: string) => {
  idMarker = idMarker || sectionMarker;
  const sections = document.querySelectorAll(`[data-type="${sectionMarker}"]`);
  if (!sections || !sections.length) return;
  sections.forEach((section, index) => {
    if (section.id) return; // 如果已经有 ID，则跳过
    section.setAttribute("id", `${idMarker}-${index + 1}`);
  });
  scrollToAnchor(); // 处理锚点跳转
};

/**
 * @function scrollToAnchor
 * @description 滚动到指定的锚点位置
 * @param {string} [anchorId] - 锚点 ID，默认为当前 URL 的 hash 值
 * @param {Partial<ScrollToAnchorOptions>} [options] - 可选的滚动配置
 * @defaults { offset: 0, retryTime: 3, delay: 200 }
 * @example
 * scrollToAnchor("section-2", { offset: 100 }) // 滚动到 ID 为 "section-2" 的元素位置，并向上偏移 100px
 * scrollToAnchor() // 滚动到当前 URL hash 对应的元素位置
 */
export const scrollToAnchor = (anchorId?: string, options?: Partial<ScrollToAnchorOptions>) => {
  const { offset = 0, retryTime = 3, delay = 200 } = options || {};
  let attempts = 0;
  const tryScroll = () => {
    if (attempts >= retryTime) return;
    attempts++;
    const urlAnchor = window.location.hash.replace("#", "");
    const target = anchorId || urlAnchor;
    const targetElement = document.getElementById(target);

    if (!targetElement) {
      setTimeout(tryScroll, delay);
      return;
    }

    const elementPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = offset ? elementPosition - offset : elementPosition;
    window.scrollTo({
      top: offsetPosition,
    });

    // 如果滚动被打断，稍后再尝试一次
    setTimeout(() => {
      const delta = Math.abs(window.pageYOffset - offsetPosition);
      if (delta > 2) {
        tryScroll();
      }
    }, delay);
  };
  tryScroll();
};

/**
 * @description 在 rem 和 px 之间互相转换
 * @param {number} value - 需要转换的数值
 * @param {'rem' | 'px'} unit - 输入值的单位：'px' 则返回对应的 rem 数值，'rem' 则返回对应的 px 数值
 * @returns {number} 转换后的数值（不含单位）
 * @example
 * convertUnit(160, 'px')  // → 16（以根字体 10px 为例，返回 16rem）
 * convertUnit(16, 'rem')  // → 160（返回 160px）
 */
export const convertUnit = (value: number, unit: "rem" | "px"): number => {
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return unit === "px" ? value / rootFontSize : value * rootFontSize;
};

/**
 * @description 将嵌套的数组扁平化
 * @param {K} key - 用于标识子数组的键
 * @param {T[]} arr - 需要扁平化的数组
 * @returns {T[]} 扁平化后的数组
 */
export const flattenArray = <T, K extends keyof T>(key: K, arr: T[]): T[] => {
  return arr.reduce<T[]>((acc, item) => {
    acc.push(item);
    const children = item[key];
    if (Array.isArray(children) && children.length > 0) {
      acc.push(...flattenArray(key, children as T[]));
    }
    return acc;
  }, []);
};

export const rebuildPagination = (opts: CommonPaginationOptions = {}) => {
  const {
    container = $("body"),
    prev: customPrev,
    next: customNext,
    pager: customPager,
    omit: customOmit,
    omitCount = 3,
    onNavigation,
    autoComplete = true,
  } = opts;
  const returnElements: { el: JQuery<HTMLElement>; prev: JQuery<HTMLElement>; next: JQuery<HTMLElement>; content: JQuery<HTMLElement> }[] = [];

  container.find(".page__pagination .pagination").each((_, el) => {
    const $pagination = $(el);
    const $wrapper = $pagination.closest(".page__pagination");
    const items = $pagination.children("li");
    if (items.length < 3) return;
    const prevLi = items.first();
    const nextLi = items.last();
    const pageItems = items.slice(1, -1);
    const totalPages = pageItems.length;
    // 构建 prev / next 按钮
    const buildNavBtn = (li: JQuery<HTMLElement>, className: string, custom?: renderEl) => {
      const isDisabled = li.hasClass("disabled");
      const inner = li.children().first();
      const href = inner.is("a") ? inner.attr("href") || "#" : "#";
      const text = inner.html();

      const $div = $(`<div class="${className}"></div>`);
      if (isDisabled) $div.addClass("disabled");

      if (custom) {
        $div.append(custom(href).clone());
      } else {
        $div.append($("<a></a>").attr("href", href).attr("title", "").html(text));
      }
      return $div;
    };
    const buildPagerItem = (li: JQuery<HTMLElement>) => {
      const $clonedLi = li.clone();
      if (!customPager) return $clonedLi;

      const $customItem = customPager($clonedLi.get(0) as HTMLElement);
      return $customItem.length ? $customItem : $clonedLi;
    };
    const buildVisibleList = () => {
      const $list = $('<ul class="list"></ul>');
      // 如果 omitCount 为 false，则显示全部页码
      if (omitCount === false) {
        pageItems.each((_, pageEl) => {
          $list.append(buildPagerItem($(pageEl as HTMLElement)));
        });
        return $list;
      }
      // 否则执行省略逻辑
      // 找到 active 页的索引（0-based）
      let activeIdx = 0;
      pageItems.each((i, el) => {
        if ($(el).hasClass("active")) activeIdx = i;
      });
      // 计算需要显示的页码索引：active 起往后 omitCount 个 + 末尾 omitCount 个
      const visible = new Set<number>();
      for (let i = activeIdx; i < Math.min(activeIdx + omitCount, totalPages); i++) {
        visible.add(i);
      }
      for (let i = Math.max(0, totalPages - omitCount); i < totalPages; i++) {
        visible.add(i);
      }
      const sorted = Array.from(visible).sort((a, b) => a - b);
      sorted.forEach((idx, i) => {
        // 索引不连续时插入省略标记
        if (i > 0 && idx - sorted[i - 1] > 1) {
          $list.append(customOmit ? customOmit.clone() : $('<li class="omit"><span>...</span></li>'));
        }
        $list.append(buildPagerItem(pageItems.eq(idx)));
      });
      return $list;
    };
    const $prev = buildNavBtn(prevLi, "prev nav-btn", customPrev);
    const $next = buildNavBtn(nextLi, "next nav-btn", customNext);

    const $content = $('<div class="pagination-wrap"></div>').append(buildVisibleList());
    if (autoComplete) {
      // 替换原始分页结构
      $wrapper.empty().append($prev, $content, $next);
    }
    // 绑定导航回调
    if (onNavigation) {
      $wrapper.on("click", "a", (e) => {
        const url = $(e.currentTarget).attr("href") || "#";
        if (onNavigation(url) === false) {
          e.preventDefault();
        }
      });
    }
    returnElements.push({
      el: $wrapper,
      prev: $prev,
      next: $next,
      content: $content,
    });
  });
  return returnElements;
};

/**
 * 清理容器内文本节点中的不间断空格，并返回适合 SplitText 的非空段落目标。
 * 当不存在非空段落时返回原容器，保证 SplitText 始终能获得有效目标。
 */
export const getSplitTextTarget = (container: JQuery<HTMLElement>, paragraphSelector = "> p"): JQuery<HTMLElement> => {
  container.each((_, element) => {
    const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();

    while (textNode) {
      const text = textNode.nodeValue;
      if (text?.includes("\u00a0")) {
        textNode.nodeValue = text.replace(/\u00a0/g, "");
      }
      textNode = walker.nextNode();
    }
  });

  const nonEmptyParagraphs = container
    .find<HTMLElement>(paragraphSelector)
    .filter((_, element) => Boolean(element.textContent?.trim()));

  return nonEmptyParagraphs.length ? nonEmptyParagraphs : container;
};

/**
 * @function generateColors
 * @description 根据输入的 base color 生成一系列明暗色调和透明度变化的颜色，适用于主题色扩展
 * @defaults { count: 3, setName: "themeColor", setTo: false, step: 10 }
 * @example window.themeColors = generateColors($("body").css("--themeColor"), { setTo: true, count: 9 });
 */
// 重载 1：hex 为 string
export function generateColors(hex: string, opts?: GenerateShadesStringOpts): { [key: string]: GenerateShadesReturnType };
// 重载 2：hex 为非空对象（至少包含一个属性）
export function generateColors<T extends { [key: string]: string }>(
  hex: NonEmptyRecord<T>,
  opts?: GenerateShadesStringOpts,
): { [key: string]: GenerateShadesReturnType };
// 重载 3：hex 为 GenerateShadesParamsType（必须包含 colors 属性），不允许传入 opts
export function generateColors(hex: GenerateShadesParamsType): { [key: string]: GenerateShadesReturnType };
export function generateColors(
  hex: GenerateShadesHexType | GenerateShadesParamsType,
  opts?: Partial<GenerateShadesParamsType>,
): { [key: string]: GenerateShadesReturnType } {
  // 类型守卫：判断 hex 是否为 GenerateShadesParamsType（即具有 colors 属性）
  const isShadesParamsType = (obj: unknown): obj is GenerateShadesParamsType & { colors: GenerateShadesHexType } => {
    return typeof obj === "object" && obj !== null && "colors" in obj;
  };
  // 使用 opts.count 默认值
  // let { count = 3, setName = "themeColor", setTo = false } = hex || opts || {};
  const { count = 3, setName = "themeColor", setTo = false, step = 10, cssAlphaPrefix = "COLOR" } = isShadesParamsType(hex) ? hex : opts || {};
  const styleElementId = "yige-generate-colors-vars";
  const injectedStyleCache = ((window as unknown as { __YIGE_GENERATE_COLORS_STYLE_CACHE__?: Record<string, string> }).__YIGE_GENERATE_COLORS_STYLE_CACHE__ ||=
    {});

  const normalizeHex = (hex: string) => {
    const value = hex.trim();
    if (/^#[0-9a-fA-F]{6}$/.test(value)) return value;
    if (/^#[0-9a-fA-F]{3}$/.test(value)) {
      const [, r, g, b] = value;
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    return value;
  };

  const normalizeAlpha = (alpha: number) => {
    return Math.max(0, Math.min(1, alpha));
  };

  const createRgba = (hex: string, alpha: number) => {
    const [r, g, b] = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${normalizeAlpha(alpha)})`;
  };

  const toCssColorToken = (name: string) => {
    const raw = name.replace(/^--/, "").replace(/Color$/i, "");
    return raw
      .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
      .replace(/_/g, "-")
      .toUpperCase();
  };

  const getUsedAlphaVariablePercents = (token: string) => {
    const percents = new Set<number>();
    const escapedPrefix = cssAlphaPrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regexp = new RegExp(`var\\(\\s*--${escapedPrefix}-${escapedToken}-ALPHA-(\\d{1,3})`, "g");
    const collect = (content: string) => {
      let match: RegExpExecArray | null;
      regexp.lastIndex = 0;
      while ((match = regexp.exec(content))) {
        const percent = Number(match[1]);
        if (Number.isFinite(percent) && percent >= 0 && percent <= 100) {
          percents.add(percent);
        }
      }
    };

    $("style").each((_, el) => collect(el.textContent || ""));
    $("[style]").each((_, el) => collect($(el).attr("style") || ""));

    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        Array.from(sheet.cssRules || []).forEach((rule) => collect(rule.cssText));
      } catch (_error) {
        // 跨域样式表不允许读取 cssRules，跳过即可。
      }
    });

    return Array.from(percents);
  };

  const setCssVariables = (variables: Record<string, string>) => {
    if (!setTo) return;
    if (setTo instanceof HTMLElement) {
      Object.keys(variables).forEach((name) => {
        setTo.style.setProperty(name, variables[name]);
      });
      return;
    }

    const selector = typeof setTo === "string" ? setTo : "body";
    Object.assign(injectedStyleCache, variables);
    let styleEl = document.getElementById(styleElementId) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleElementId;
      document.head.appendChild(styleEl);
    }
    const declarations = Object.keys(injectedStyleCache)
      .map((name) => `  ${name}: ${injectedStyleCache[name]};`)
      .join("\n");
    styleEl.textContent = `${selector} {\n${declarations}\n}`;
  };

  // 将 hex 字符串转换为 HSL
  const hexToHsl = (hex: string) => {
    hex = normalizeHex(hex);
    const r = parseInt(hex.substring(1, 3), 16) / 255;
    const g = parseInt(hex.substring(3, 5), 16) / 255;
    const b = parseInt(hex.substring(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h: number, s: number;
    const l = (max + min) / 2;
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) {
        h = (g - b) / d + (g < b ? 6 : 0);
      } else if (max === g) {
        h = (b - r) / d + 2;
      } else {
        h = (r - g) / d + 4;
      }
      h /= 6;
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
  };

  // 将 HSL 转换为 hex 字符串
  const hslToHex = (h: number, s: number, l: number) => {
    s /= 100;
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r: number, g: number, b: number;
    if (h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (h < 300) {
      r = x;
      g = 0;
      b = c;
    } else {
      r = c;
      g = 0;
      b = x;
    }
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
  };

  // hex -> rgb 数组
  const hexToRgb = (hex: string): [number, number, number] => {
    hex = normalizeHex(hex);
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    return [r, g, b];
  };

  // 生成明暗色调
  const generate = (h: number, s: number, l: number, baseHex: string) => {
    const lighter: string[] = [];
    const darker: string[] = [];
    const alpha: string[] = [];
    let currentLighterValue = l;
    // 将 step（例如 10）转换为比率（例如 0.1）
    const lightenStepRatio = step / 100;

    for (let i = 0; i < count; i++) {
      // 基于当前亮度值计算下一个更浅的色调
      currentLighterValue = currentLighterValue + (100 - currentLighterValue) * lightenStepRatio;
      lighter.push(hslToHex(h, s, Math.min(currentLighterValue, 100)));
    }

    let currentDarkerValue = l;
    // 将 step（例如 10）转换为比率（例如 0.1）
    const darkenStepRatio = step / 100;

    for (let i = 0; i < count; i++) {
      // 基于当前亮度值计算下一个更深的色调
      // currentDarkerValue * (1 - darkenStepRatio) 等效于 currentDarkerValue - currentDarkerValue * darkenStepRatio
      currentDarkerValue = currentDarkerValue * (1 - darkenStepRatio);
      darker.push(hslToHex(h, s, Math.max(currentDarkerValue, 0)));
    }
    // 生成透明度序列：与 step/ratio 一致，从 1 逐步逼近 0
    if (baseHex) {
      const [r, g, b] = hexToRgb(baseHex);
      let currentAlpha = 1;
      const alphaStepRatio = step / 100;
      for (let i = 0; i < count; i++) {
        currentAlpha = currentAlpha * (1 - alphaStepRatio);
        const a = Math.max(0, Math.min(1, parseFloat(currentAlpha.toFixed(4))));
        alpha.push(`rgba(${r}, ${g}, ${b}, ${a})`);
      }
    }
    return { lighter, darker, alpha, color: baseHex };
  };

  const generateForHex = (hex: string) => {
    hex = normalizeHex(hex);
    const { h, s, l } = hexToHsl(hex);
    return generate(h, s, l, hex);
  };

  const generateForObject = (obj: { [key: string]: string }) => {
    const result: { [key: string]: GenerateShadesReturnType } = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = generateForHex(obj[key]);
      }
    }
    if (setTo) {
      const variables: Record<string, string> = {};
      Object.keys(result).forEach((key) => {
        const { darker, lighter, alpha, color } = result[key];
        const colorToken = toCssColorToken(key || setName);
        variables[`--${setName}`] = color;
        darker.forEach((color, idx) => {
          variables[`--${key}-dark-${idx + 1}`] = color;
        });
        lighter.forEach((color, idx) => {
          variables[`--${key}-light-${idx + 1}`] = color;
        });
        alpha.forEach((color, idx) => {
          variables[`--${key}-alpha-${idx + 1}`] = color;
        });
        getUsedAlphaVariablePercents(colorToken).forEach((percent) => {
          variables[`--${cssAlphaPrefix}-${colorToken}-ALPHA-${percent}`] = createRgba(color, percent / 100);
        });
      });
      setCssVariables(variables);
    }
    return result;
  };

  // 如果 hex 是 GenerateShadesParamsType（必须包含 colors 属性）
  if (isShadesParamsType(hex)) {
    const colors = hex.colors;
    if (typeof colors === "string") {
      return generateForObject({ [setName]: colors });
    } else {
      return generateForObject(colors);
    }
  }

  // 如果 hex 为 string，则直接生成
  if (typeof hex === "string") {
    return generateForObject({ [setName]: hex });
  }

  // 如果 hex 为对象（注意此处对象已经被限制为非空）
  return generateForObject(hex);
}

export const padZero = <T>(num: T): T => {
  let res: T;
  if (typeof num === "number" && num < 10) {
    res = ("0" + num) as T;
  } else {
    res = num as T;
  }
  return res as T;
};
